/**
 * PLPThumbVideo - muted, looping card videos on the Algolia PLP (products tagged `plp-thumb-video`).
 * algolia-collection.liquid injects the video and calls attach(); it loads near the viewport, plays only on screen.
 */
window.PLPThumbVideo = {
  videos: [],
  _prepareObserver: null,
  _playObserver: null,

  // Fetch a screenful early; play only once actually visible.
  PREPARE_MARGIN: '400px 0px',
  PLAY_RATIO: 0.25,

  // The turntable's SVG marks (not the shared white PNGs, which vanish on white product shots); CSS picks one.
  ICON_SVG: '<svg class="plp-thumb-video-icon" viewBox="0 0 26 26" fill="none" aria-hidden="true" focusable="false">' +
    '<g class="plp-thumb-video-mark plp-thumb-video-mark--pause">' +
      '<path d="M10 8H12V18H10V8Z" fill="currentColor"/>' +
      '<path d="M14 8H16V18H14V8Z" fill="currentColor"/>' +
    '</g>' +
    '<path class="plp-thumb-video-mark plp-thumb-video-mark--play" d="M10.5 8.5L17 13L10.5 17.5V8.5Z" fill="currentColor"/>' +
  '</svg>',

  reduceMotion: function() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  },

  init: function() {
    var self = this;
    var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mq || !mq.addEventListener) return;
    // Toggling the OS setting mid-session takes effect without a reload.
    mq.addEventListener('change', function() {
      self.videos.forEach(function(video) {
        var state = video._plpThumbVideo;
        if (!state) return;
        if (self.reduceMotion()) self.pause(video);
        else if (state.inView) self.play(video);
      });
    });
  },

  attach: function(video, controlsHost) {
    if (!video || video._plpThumbVideo) return;
    this.sweep();
    video._plpThumbVideo = { hidden: false, inView: false, prepared: false, userPaused: false, control: null };
    this.videos.push(video);

    if (controlsHost) this.addControl(video, controlsHost);

    // Revealed on `playing` only, so refused autoplay leaves the identical picture underneath showing.
    video.addEventListener('playing', function() {
      video.classList.add('plp-thumb-video--ready');
    });

    if (!('IntersectionObserver' in window)) {
      this.prepare(video);
      this.play(video);
      return;
    }
    this.observers();
    this._prepareObserver.observe(video);
    this._playObserver.observe(video);
  },

  observers: function() {
    var self = this;
    if (this._prepareObserver) return;

    this._prepareObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        self.prepare(entry.target);
        self._prepareObserver.unobserve(entry.target);
      });
    }, { rootMargin: this.PREPARE_MARGIN });

    this._playObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        var state = entry.target._plpThumbVideo;
        if (!state) return;
        state.inView = entry.isIntersecting;
        if (entry.isIntersecting) self.play(entry.target);
        else self.pause(entry.target);
      });
    }, { threshold: this.PLAY_RATIO });
  },

  prepare: function(video, force) {
    var state = video._plpThumbVideo;
    if (!state || state.prepared) return;
    // Reduced motion downloads nothing unless someone presses play (`force`).
    if (!force && this.reduceMotion()) return;
    state.prepared = true;
    var poster = video.getAttribute('data-poster');
    if (poster && !video.getAttribute('poster')) video.setAttribute('poster', poster);
    var src = video.getAttribute('data-src');
    if (!src) return;
    video.setAttribute('preload', 'metadata');
    video.setAttribute('src', src);
  },

  play: function(video) {
    var state = video._plpThumbVideo;
    // A user's pause outranks the observer, so scrolling back does not restart it.
    if (!state || state.hidden || state.userPaused) return;
    if (this.reduceMotion()) return;
    this.prepare(video);
    this.start(video);
  },

  start: function(video) {
    // The property, not the attribute: unmuted autoplay is always refused.
    video.muted = true;
    var played = video.play();
    if (played && typeof played.catch === 'function') played.catch(function() {});
  },

  pause: function(video) {
    try { video.pause(); } catch (e) {}
  },

  // Called by PLPMobileSwipe when a card swipes off (and back onto) slide 1.
  setHidden: function(video, hidden) {
    var state = video && video._plpThumbVideo;
    if (!state) return;
    state.hidden = !!hidden;
    if (state.control) {
      state.control.classList.toggle('plp-thumb-video-controls--hidden', state.hidden);
    }
    if (state.hidden) this.pause(video);
    else if (state.inView) this.play(video);
  },

  // Goes in the image container, not the wrapper: that is the PDP link, and a button inside it would navigate.
  addControl: function(video, host) {
    var self = this;
    var state = video._plpThumbVideo;

    var control = document.createElement('div');
    control.className = 'oa-video-controls-wrapper plp-thumb-video-controls';
    control.innerHTML = '<button type="button" class="oa-video-controls__play">' + this.ICON_SVG + '</button>';

    var button = control.querySelector('button');
    button.addEventListener('click', function(event) {
      event.preventDefault();
      event.stopPropagation();
      self.toggle(video);
    });

    // Driven off media events, so the icon stays true when the observer pauses or autoplay is refused.
    video.addEventListener('play', function() { self.syncControl(video); });
    video.addEventListener('pause', function() { self.syncControl(video); });

    state.control = control;
    host.appendChild(control);
    this.syncControl(video);
  },

  syncControl: function(video) {
    var state = video._plpThumbVideo;
    if (!state || !state.control) return;
    state.control.classList.toggle('plp-thumb-video-controls--paused', video.paused);
    var button = state.control.querySelector('button');
    // Named for what pressing it does, in the shop's language (a11y-announcer.liquid).
    var labels = window.oaA11yLabels || {};
    if (button) button.setAttribute('aria-label', video.paused ? (labels.videoPlay || 'Play') : (labels.videoPause || 'Pause'));
  },

  toggle: function(video) {
    var state = video._plpThumbVideo;
    if (!state) return;
    if (video.paused) {
      state.userPaused = false;
      this.prepare(video, true);
      this.start(video);
    } else {
      state.userPaused = true;
      this.pause(video);
    }
  },

  // Drop videos whose card a re-render removed (PLP filters, search keystrokes), so the observers do not leak.
  sweep: function() {
    var self = this;
    this.videos = this.videos.filter(function(video) {
      if (video.isConnected) return true;
      if (self._prepareObserver) self._prepareObserver.unobserve(video);
      if (self._playObserver) self._playObserver.unobserve(video);
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (e) {}
      delete video._plpThumbVideo;
      return false;
    });
  }
};

window.PLPThumbVideo.init();
